import Button from "@ingka/button";
import Text from "@ingka/text";
import { useEffect, useRef } from "react";
import { useNavigate, useRouteError } from "react-router";
import { useAuth } from "@/hooks/auth";
import { classifyRouteError, isKnownAuthCode } from "@/errors/routeError";
import { sendErrorToSentry } from "@/sentry";

const UNKNOWN_MESSAGE = {
  title: "Oops!",
  body: "Sorry, an unexpected error has occurred. Try to refresh the page or try again later.",
};

function useReportRouteError(err: ReturnType<typeof classifyRouteError>) {
  const { user } = useAuth();
  const reported = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Expected authorization denials are user-facing outcomes, not system
    // faults; skip Sentry so alerting isn't flooded. Only report UNKNOWN.
    if (isKnownAuthCode(err.code)) return;

    const key = `${err.code}:${err.uploadId ?? ""}:${window.location.pathname}`;
    if (reported.current === key) return;
    reported.current = key;
    sendErrorToSentry(err, {
      tags: {
        errorCode: err.code,
        scope: err.scope,
        httpStatus: String(err.status ?? 0),
      },
      extra: {
        uploadId: err.uploadId,
        userId: user.id,
        pathname: window.location.pathname,
        detail: err.detail,
      },
    });
  }, [err, user.id]);
}

export function ErrorPage() {
  const err = classifyRouteError(useRouteError());
  useReportRouteError(err);
  const { title, body } = err.title && err.body ? err : UNKNOWN_MESSAGE;
  const navigate = useNavigate();

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-space100">
      <Text tagName="h1" headingSize="l">
        {title}
      </Text>
      <Text tagName="p" bodySize="m">
        {body}
      </Text>
      {err.code === "UNKNOWN" && err.detail ? (
        <Text tagName="p" bodySize="s" className="italic">
          {err.detail}
        </Text>
      ) : null}
      <Button
        text="Back to overview"
        type="emphasised"
        onClick={() => navigate("/overview")}
      />
    </div>
  );
}
